// Report processing error messaging
// ----------------------------------
// When Patient Report generation fails, the documents dashboard historically
// showed a single generic line — "An error occurred. Please try again." — for
// every failure cause. That is unhelpful for the one failure a clinician can
// actually fix themselves: a missing or invalid Contract Type
// (customFields.assessmentType), which the TOC report renderer requires to pick
// the correct template variant.
//
// This module is the single source of truth for:
//   1. The stable reason CODES we persist on the patient document
//      (dataModel.6_manageReports.prReportProcessingError) when generation fails.
//   2. Classifying a raised Cloud Function error into one of those codes.
//   3. Mapping a code to the user-facing message shown in the documents UI.
//
// Keeping all three together guarantees the writer (TriggerPatientReportGeneration)
// and the readers (DocumentsDashboard / AdditionalDocuments) never disagree.

// Stable codes persisted to Firestore. Treat these as an enum — never store free
// text, so the UI mapping stays authoritative and translatable.
export const REPORT_PROCESSING_ERROR_REASONS = Object.freeze({
  MISSING_CONTRACT_TYPE: 'missing_contract_type',
  // Written by the server-side approval gate (generatePatientReport) when the
  // report would have consumed populated-but-unapproved clinician paragraphs,
  // is missing an approved required section, or is missing an approved weekly
  // medication instruction the titration schedule expects. This is the
  // clinician-fixable "go back and approve the outstanding paragraphs" case.
  UNAPPROVED_CONTENT: 'unapproved_content',
  GENERIC: 'generic',
});

// The fallback message, identical to the original generic wording so nothing
// regresses for failures we can't attribute to a specific, user-fixable cause.
const GENERIC_MESSAGE = 'An error occurred. Please try again.';

// Specific, actionable messages keyed by reason code.
const MESSAGE_BY_REASON = Object.freeze({
  [REPORT_PROCESSING_ERROR_REASONS.MISSING_CONTRACT_TYPE]:
    'This patient has no valid Contract Type set, which the report needs. '
    + 'Set the Contract Type in the patient\u2019s details, then try again.',
  [REPORT_PROCESSING_ERROR_REASONS.UNAPPROVED_CONTENT]:
    'The Patient Report was not generated because some paragraphs have not been '
    + 'approved. Open the relevant report sections, approve the outstanding '
    + 'paragraphs, then try again.',
});

// Human-readable section labels for the outstanding-codes detail the server
// persists (dataModel.6_manageReports.prReportProcessingErrorDetail).
const SECTION_LABELS = Object.freeze({
  ci: 'Clinical Interview',
  ssi: 'Semi-Structured Interview',
  dd: 'Diagnostic Decision',
  pad: 'Post-Assessment Discussion',
});

/**
 * Classify a Cloud Function error (from the generate/render chain) into a stable
 * reason code we can persist and later map to a message.
 *
 * We detect the "missing/invalid Contract Type" case two ways for robustness:
 *   - the callable error code is FAILED_PRECONDITION (how render.py raises it), or
 *   - the message mentions the Contract Type / assessmentType field (covers
 *     resolve_contract_key and validate_contract_type wording).
 * Everything else is GENERIC.
 *
 * @param {{code?: string, message?: string}} error
 * @returns {string} one of REPORT_PROCESSING_ERROR_REASONS
 */
export const classifyReportError = (error) => {
  const code = (error?.code || '').toString().toLowerCase();
  const message = (error?.message || '').toString().toLowerCase();

  // The approval gate and the contract-type validator both raise
  // FAILED_PRECONDITION, so we disambiguate on the message. Check the
  // approval-gate case FIRST because its wording is more specific.
  const mentionsUnapproved =
    message.includes('not been approved') ||
    message.includes('have not been approved') ||
    message.includes('paragraphs have not');
  if (mentionsUnapproved) {
    return REPORT_PROCESSING_ERROR_REASONS.UNAPPROVED_CONTENT;
  }

  const isPreconditionFailure = code.includes('failed-precondition');
  const mentionsContractType =
    message.includes('contract type') || message.includes('assessmenttype');

  if (isPreconditionFailure || mentionsContractType) {
    return REPORT_PROCESSING_ERROR_REASONS.MISSING_CONTRACT_TYPE;
  }
  return REPORT_PROCESSING_ERROR_REASONS.GENERIC;
};

/**
 * Turn the server-persisted error detail for the UNAPPROVED_CONTENT case into a
 * short, human-readable summary of which sections still have outstanding
 * paragraphs, e.g. "Post-Assessment Discussion (2), Clinical Interview (1)".
 * Returns '' when there is nothing meaningful to add.
 *
 * @param {{ outstandingCodesBySection?: Object, missingSections?: string[] }} [detail]
 * @returns {string}
 */
export const summariseOutstandingDetail = (detail) => {
  if (!detail || typeof detail !== 'object') return '';
  const parts = [];

  const bySection = detail.outstandingCodesBySection || {};
  Object.keys(bySection).forEach((label) => {
    const codes = bySection[label] || [];
    if (codes.length > 0) {
      parts.push(`${label} (${codes.length})`);
    }
  });

  // missingSections uses short prefixes (ci/ssi/dd/pad); map to labels and only
  // add ones not already represented by the outstanding-codes summary above.
  const missing = detail.missingSections || [];
  missing.forEach((prefix) => {
    const label = SECTION_LABELS[prefix] || prefix;
    if (!parts.some((p) => p.startsWith(label))) {
      parts.push(label);
    }
  });

  return parts.join(', ');
};

/**
 * Map a persisted reason code to the user-facing message. Unknown/missing codes
 * fall back to the generic message so older patient documents (written before
 * this field existed) keep showing the original wording.
 *
 * When a detail object is supplied for the UNAPPROVED_CONTENT case, the message
 * is enriched with the specific sections that still have outstanding paragraphs
 * so the clinician knows exactly where to go.
 *
 * @param {string} [reasonCode]
 * @param {Object} [detail] optional prReportProcessingErrorDetail payload
 * @returns {string}
 */
export const getReportProcessingErrorMessage = (reasonCode, detail) => {
  const base = MESSAGE_BY_REASON[reasonCode] || GENERIC_MESSAGE;

  if (reasonCode === REPORT_PROCESSING_ERROR_REASONS.UNAPPROVED_CONTENT) {
    const summary = summariseOutstandingDetail(detail);
    if (summary) {
      return `${base} Outstanding: ${summary}.`;
    }
  }

  return base;
};
